'use client';

import React, { useState } from 'react';
import { triggerGooglePreferredSource } from '@/lib/googlePreferredSource';
import { trackClick } from '@/lib/analytics';

interface GooglePreferredSourceButtonProps {
  className?: string;
  lang?: string;
}

const GoogleIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg
    className={className}
    viewBox='0 0 24 24'
    aria-hidden='true'
  >
    <path
      fill='#4285F4'
      d='M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z'
    />
    <path
      fill='#34A853'
      d='M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z'
    />
    <path
      fill='#FBBC05'
      d='M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z'
    />
    <path
      fill='#EA4335'
      d='M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z'
    />
  </svg>
);

const GooglePreferredSourceButton: React.FC<GooglePreferredSourceButtonProps> = ({
  className = '',
  lang = 'en',
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    trackClick('google_preferred_source_btn', 'Interaction', 'Click', 'google_preferred_source');
    setIsLoading(true);
    try {
      await triggerGooglePreferredSource({ theme: 'light', lang });
    } catch (e) {
      console.error('Error triggering Google Preferred Source:', e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      aria-label='Add Public App as a preferred source on Google'
      title='Add Public App as a preferred source in Google Search'
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-white text-gray-800 border border-gray-200/90 hover:border-gray-300 hover:bg-gray-50 hover:shadow-xs active:scale-98 transition-all duration-150 shadow-2xs cursor-pointer disabled:opacity-60 shrink-0 ${className}`}
    >
      {isLoading ? (
        <span className='w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin' />
      ) : (
        <GoogleIcon className='w-4 h-4 shrink-0' />
      )}
      <span className='font-medium text-gray-700 whitespace-nowrap'>
        Follow on Google
      </span>
      <span className='hidden sm:inline-block text-[10px] font-semibold tracking-wide text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md'>
        Preferred Source
      </span>
    </button>
  );
};

export default GooglePreferredSourceButton;
